import * as THREE from "three";
import {
  attachFoamAttributes,
  createFoamBlockGeometry,
  createFoamBlockOutline,
  FOAM_OCCLUSION_ATTRIBUTE,
} from "./foamBlockGeometry";
import {
  noppenHeight,
  noppenShade,
  noppenSlope,
  SPIKE_BASE_MM,
  SPIKE_PITCH_MM,
} from "./noppenSurface";

type Axis = 0 | 1 | 2;

/**
 * Sample phases across a nub. The next cell supplies the endpoint at 1.
 *
 * Smooth normals cannot round a polygonal silhouette. The old six uniform
 * intervals missed the 20 mm surface by up to 1.61 mm between vertices and
 * joined the tip with a 42-degree chord. Eight intervals, closer together at
 * the tips and valleys, reduce those figures to 0.62 mm and 28 degrees while
 * keeping the same continuous height function, pitch and thickness. The
 * half-pitch/reflection symmetries are deliberate: opposing cuts still match.
 * Only the visible face needs this grid; the backing remains a perimeter fan.
 */
const SPIKE_SAMPLE_PHASES = [0, 0.08, 0.25, 0.42, 0.5, 0.58, 0.75, 0.92];
const SEGMENTS_PER_SPIKE = SPIKE_SAMPLE_PHASES.length;

/**
 * How far a vertex may sit from the nominal face and still count as being on it.
 */
const FACE_EPSILON_MM = 1e-4;

/**
 * How much nub a pad this thick has left above its base plate, in mm.
 *
 * The one place the height is derived, because two things have to agree on it:
 * the geometry displaces the cap by exactly this, and — for the pads whose
 * geometry cannot carry a nub — `getEggboxFoamMaterial` bakes its painted-on
 * version from it. A pad with cutouts sits next to one without, and a painted
 * nub of a different height than the modelled one beside it is the mismatch
 * this exists to prevent.
 */
export function spikedPadNubHeight(thickness: number): number {
  return thickness - SPIKE_BASE_MM;
}

/**
 * Whether a pad this thick is modelled with real nubs.
 *
 * Exported for the same reason `slottedPadHasGrooves` is: the renderer must
 * not hand a pad that fell back to a plain block the material that expects
 * the nubs to be in the mesh.
 */
export function spikedPadHasNubs(thickness: number): boolean {
  return spikedPadNubHeight(thickness) > 0;
}

function spikeCells(span: number): number {
  return Math.max(1, Math.round(span / SPIKE_PITCH_MM));
}

/**
 * One canonical Float32 coordinate per grid line, shared by every face.
 *
 * Reverse the index on a reversed face, not the floating-point arithmetic:
 * otherwise fractional widths can leave opposite roundoff on a shared edge.
 */
function spikeCoordinates(span: number, cells: number): Float32Array {
  const segments = cells * SEGMENTS_PER_SPIKE;
  const coordinates = new Float32Array(segments + 1);
  const pitch = span / cells;
  for (let i = 0; i < segments; i++) {
    const cell = Math.floor(i / SEGMENTS_PER_SPIKE);
    const phase = SPIKE_SAMPLE_PHASES[i % SEGMENTS_PER_SPIKE] as number;
    coordinates[i] = (cell + phase) * pitch - span / 2;
  }
  coordinates[segments] = span / 2;
  return coordinates;
}

/**
 * A nonuniform nub grid and four side strips, with a perimeter fan for +y.
 *
 * The back retains every side-strip segment, rather than just four corners:
 * each rim edge meets one side triangle without a T-junction. Copy the rim
 * from the actual side vertices. No backing interior or throwaway UV buffer
 * is allocated; groups keep BoxGeometry's face order and outward winding.
 */
function createSpikedPadTopology(
  thickness: number,
  xCoordinates: Float32Array,
  zCoordinates: Float32Array,
): { geometry: THREE.BufferGeometry; nubVertexStart: number } {
  const segmentsX = xCoordinates.length - 1;
  const segmentsZ = zCoordinates.length - 1;
  const coordinates = [
    xCoordinates,
    new Float32Array([-thickness / 2, thickness / 2]),
    zCoordinates,
  ] as const;
  const perimeter = 2 * (segmentsX + segmentsZ);
  const sideVertices = 4 * (segmentsX + segmentsZ + 2);
  const vertexCount =
    (segmentsX + 1) * (segmentsZ + 1) + sideVertices + perimeter + 1;
  const indexCount = 6 * segmentsX * segmentsZ + 9 * perimeter;
  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  // Like Three, reserve 65535 for fixed-index primitive restart.
  const indices =
    vertexCount > 65535
      ? new Uint32Array(indexCount)
      : new Uint16Array(indexCount);
  const geometry = new THREE.BufferGeometry();
  let vertexCursor = 0;
  let indexCursor = 0;

  function buildPlane(
    u: Axis,
    v: Axis,
    w: Axis,
    udir: number,
    vdir: number,
    outward: number,
    materialIndex: number,
  ): number {
    const firstVertex = vertexCursor;
    const firstIndex = indexCursor;
    const alongU = coordinates[u];
    const alongV = coordinates[v];
    const alongW = coordinates[w];
    const gridX = alongU.length - 1;
    const gridY = alongV.length - 1;
    const plane = alongW[outward > 0 ? alongW.length - 1 : 0] as number;
    const row = gridX + 1;

    for (let iy = 0; iy <= gridY; iy++) {
      const y = alongV[vdir > 0 ? iy : gridY - iy] as number;
      for (let ix = 0; ix <= gridX; ix++) {
        const x = alongU[udir > 0 ? ix : gridX - ix] as number;
        const offset = vertexCursor++ * 3;
        positions[offset + u] = x;
        positions[offset + v] = y;
        positions[offset + w] = plane;
        normals[offset + w] = outward;
      }
    }

    for (let iy = 0; iy < gridY; iy++) {
      for (let ix = 0; ix < gridX; ix++) {
        const a = firstVertex + ix + row * iy;
        const b = firstVertex + ix + row * (iy + 1);
        const c = firstVertex + ix + 1 + row * (iy + 1);
        const d = firstVertex + ix + 1 + row * iy;
        indices[indexCursor++] = a;
        indices[indexCursor++] = b;
        indices[indexCursor++] = d;
        indices[indexCursor++] = b;
        indices[indexCursor++] = c;
        indices[indexCursor++] = d;
      }
    }

    geometry.addGroup(firstIndex, indexCursor - firstIndex, materialIndex);
    return firstVertex;
  }

  const px = buildPlane(2, 1, 0, -1, -1, 1, 0);
  const nx = buildPlane(2, 1, 0, 1, -1, -1, 1);

  // Reserve only the ring and center, keeping BoxGeometry's six group slots.
  const backVertex = vertexCursor;
  const backIndex = indexCursor;
  vertexCursor += perimeter + 1;
  indexCursor += perimeter * 3;
  geometry.addGroup(backIndex, perimeter * 3, 2);

  const nubVertexStart = buildPlane(0, 2, 1, 1, -1, -1, 3);
  const pz = buildPlane(0, 1, 2, 1, -1, 1, 4);
  const nz = buildPlane(0, 1, 2, -1, -1, -1, 5);

  // The first row of each strip is on +y. Walk it in outward +y winding,
  // leaving each row's last corner to the following strip.
  const strips: ReadonlyArray<readonly [number, number]> = [
    [nx, segmentsZ],
    [pz, segmentsX],
    [px, segmentsZ],
    [nz, segmentsX],
  ];
  let rimVertex = backVertex;
  for (const [firstVertex, segments] of strips) {
    for (let i = 0; i < segments; i++) {
      const source = (firstVertex + i) * 3;
      const target = rimVertex++ * 3;
      positions[target] = positions[source] as number;
      positions[target + 1] = positions[source + 1] as number;
      positions[target + 2] = positions[source + 2] as number;
      normals[target + 1] = 1;
    }
  }
  const center = backVertex + perimeter;
  positions[center * 3 + 1] = thickness / 2;
  normals[center * 3 + 1] = 1;
  for (let i = 0; i < perimeter; i++) {
    const offset = backIndex + i * 3;
    indices[offset] = center;
    indices[offset + 1] = backVertex + i;
    indices[offset + 2] = backVertex + ((i + 1) % perimeter);
  }

  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
  return { geometry, nubVertexStart };
}

/**
 * Noppenschaum als echte Geometrie: eine Grundplatte mit aufgesetzten Noppen.
 *
 * The bumps used to live only in the normal, occlusion and roughness maps, and
 * a map can never put them in the silhouette — the pad's edge stayed a straight
 * line no matter how convincing its face looked. Parallax cannot fix that
 * either; it moves texels around inside a triangle and never outside one.
 *
 * The nubs are the box's own face grid pushed out along the surface in
 * `noppenSurface`, which is where their shape is explained. Three things are
 * baked onto every vertex of that face while it is displaced, and none of them
 * cost anything at render time:
 *
 * - the normal, exactly, from the surface gradient;
 * - the pore coordinate, in millimetres, projected straight down onto the
 *   face. The material otherwise picks a projection axis per vertex from the
 *   vertex normal, and on a flank steeper than 45° that pick flips to a side
 *   axis: two thirds of the triangles on the old nubs had corners that
 *   disagreed, and smeared the pores between projections. The 1.9x stretch
 *   from projecting a 58° flank from above is invisible at 0.2 mm pores;
 * - the occlusion between nubs, which the environment lighting cannot
 *   produce and screen-space shading cannot preserve for off-screen neighbors.
 *
 * The nubs go on local -y, which is the face that shows. `rotateOntoSide`
 * turns local +y onto the side's outward normal and the slab hangs from the
 * inner surface of the panel it is glued to, so +y is the glued face and -y
 * points into the case.
 *
 * The peaks stay at the pad's nominal face so its stated thickness still reads
 * off the silhouette; it is the valleys that cut back into it.
 *
 * The side faces carry their own copy of the bottom row at the same x/z, and
 * the displacement is a function of x/z alone, so they move with it and the box
 * stays closed without any welding.
 *
 * @param width Pad width in mm, along x.
 * @param thickness Pad thickness in mm, along y.
 * @param depth Pad depth in mm, along z.
 */
export function createSpikedPadGeometry(
  width: number,
  thickness: number,
  depth: number,
): THREE.BufferGeometry {
  const spike = spikedPadNubHeight(thickness);
  if (!spikedPadHasNubs(thickness)) {
    // Thinner than the base slab of the sheet it would be cut from: there is no
    // nub left to stand on it.
    return createFoamBlockGeometry(width, thickness, depth);
  }

  const cellsX = spikeCells(width);
  const cellsZ = spikeCells(depth);
  const pitchX = width / cellsX;
  const pitchZ = depth / cellsZ;
  const segmentsX = cellsX * SEGMENTS_PER_SPIKE;
  const segmentsZ = cellsZ * SEGMENTS_PER_SPIKE;
  const { geometry, nubVertexStart } = createSpikedPadTopology(
    thickness,
    spikeCoordinates(width, cellsX),
    spikeCoordinates(depth, cellsZ),
  );

  const position = geometry.attributes.position as THREE.BufferAttribute;
  const normal = geometry.attributes.normal as THREE.BufferAttribute;
  const tip = -thickness / 2;
  const bounds = { width, depth };

  // Every vertex on the tip face, and the side rows that hang off it.
  for (let i = 0; i < position.count; i++) {
    if (Math.abs(position.getY(i) - tip) > FACE_EPSILON_MM) continue;
    const x = position.getX(i) + width / 2;
    const z = position.getZ(i) + depth / 2;
    position.setY(i, tip + spike * (1 - noppenHeight(x, z, pitchX, pitchZ)));
  }

  // The sides keep their axis-aligned normals — a side face is still a
  // vertical plane, only its bottom edge follows the profile — so the
  // projection below reads them as they are.
  // The nub face still has its -y normals here, so its final x/z UVs are
  // already written before the analytical normals tilt the flanks.
  attachFoamAttributes(geometry);

  const occlusion = geometry.getAttribute(
    FOAM_OCCLUSION_ATTRIBUTE,
  ) as THREE.BufferAttribute;
  const n = new THREE.Vector3();
  const shade = shadeLookup(pitchX, pitchZ, spike, bounds);

  const row = segmentsX + 1;
  const faceVertices = row * (segmentsZ + 1);
  for (let vertex = 0; vertex < faceVertices; vertex++) {
    const i = nubVertexStart + vertex;
    const px = position.getX(i);
    const pz = position.getZ(i);
    const x = px + width / 2;
    const z = pz + depth / 2;

    // The face is the graph y = tip + spike * (1 - h(x, z)), and it faces -y.
    const [hx, hz] = noppenSlope(x, z, pitchX, pitchZ);
    n.set(-spike * hx, -1, -spike * hz).normalize();
    normal.setXYZ(i, n.x, n.y, n.z);

    // Physical sample indices, not round(coordinate / pitch * count): that
    // assumes uniform spacing and aliases phases on a nonuniform grid.
    const phaseX = (vertex % row) % SEGMENTS_PER_SPIKE;
    const phaseZ = (segmentsZ - Math.floor(vertex / row)) % SEGMENTS_PER_SPIKE;
    occlusion.setX(i, shade(x, z, phaseX, phaseZ));
  }

  normal.needsUpdate = true;
  position.needsUpdate = true;
  return geometry;
}

/**
 * The occlusion at a point of the face, tabulated by its position within a nub.
 *
 * The horizon test costs sixty-odd evaluations of the surface, and a large pad
 * has ten thousand vertices on its face; done plainly that was tens of
 * milliseconds on the main thread per pad. But the surface is periodic, and so
 * is the grid the vertices sit on: the pitch is `width / cellsX` by
 * construction, so every interior vertex lands on one of the
 * `SEGMENTS_PER_SPIKE` divisions of a nub and shares its answer with the
 * corresponding vertex of every other nub. Only the outermost ring sees the
 * edge of the pad, and only it is tested on its own.
 *
 * The answer set is a fixed `SEGMENTS_PER_SPIKE²` table indexed by the actual
 * integer grid indices. Physical distance no longer encodes the sample index
 * because the grid is nonuniform. No string keys or per-vertex horizon search
 * are needed away from the perimeter.
 */
function shadeLookup(
  pitchX: number,
  pitchZ: number,
  spike: number,
  bounds: { width: number; depth: number },
): (x: number, z: number, phaseX: number, phaseZ: number) => number {
  const reach = Math.max(pitchX, pitchZ);
  const table = new Float64Array(SEGMENTS_PER_SPIKE * SEGMENTS_PER_SPIKE);
  const known = new Uint8Array(table.length);
  return (x, z, phaseX, phaseZ) => {
    const nearRim =
      x < reach ||
      z < reach ||
      x > bounds.width - reach ||
      z > bounds.depth - reach;
    if (nearRim) return noppenShade(x, z, pitchX, pitchZ, spike, bounds);

    const slot = phaseZ * SEGMENTS_PER_SPIKE + phaseX;
    if (known[slot] === 0) {
      table[slot] = noppenShade(x, z, pitchX, pitchZ, spike);
      known[slot] = 1;
    }
    return table[slot] as number;
  };
}

/**
 * Kantenlinien für ein Noppenpolster: nur die Grundplatte.
 *
 * `EdgesGeometry` draws every dihedral above `PART_EDGE_THRESHOLD_DEG`, and a
 * nub's flanks are far above it — outlining the mesh itself would lay a black
 * wireframe over the whole pad. The cut edges of the sheet are the base slab's,
 * so that is what gets drawn — the slab sits on +y, above the nubs.
 *
 * Without nubs the pad is a rounded block, and takes that block's outline.
 */
export function createSpikedPadOutline(
  width: number,
  thickness: number,
  depth: number,
): THREE.BufferGeometry {
  const spike = spikedPadNubHeight(thickness);
  if (!spikedPadHasNubs(thickness)) {
    return createFoamBlockOutline(width, thickness, depth);
  }
  return new THREE.BoxGeometry(width, SPIKE_BASE_MM, depth).translate(
    0,
    spike / 2,
    0,
  );
}
